import { type Getter, type Setter } from 'jotai'
import { filtersPagedListAtom, filtersQueryAtom } from '../state/state'
import { copyFilter, getSearchFilters } from '../services/services'
import { getAllCategories } from './categories'
import { type FilterPagedListQuery } from '../../../types/beFiltersApi'
import {
  errorUpdater,
  loaderUpdater,
  pageState,
  statusUpdater,
} from '../../../state/page'
import { logAppError } from '../../../helpers/logger'
import { notifySuccess } from '../../../helpers/notifyToast'

export const searchFilters = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  set(pageState, statusUpdater('fetching'))
  try {
    const query = get(filtersQueryAtom)
    let result = await getSearchFilters(query)
    // if we overshot last page, go back
    if (result.Items.length === 0 && result.TotalItems > 0) {
      const updatedQuery = {
        ...query,
        pageNumber: Math.ceil(result.TotalItems / result.PageSize),
      }
      set(filtersQueryAtom, updatedQuery)
      result = await getSearchFilters(updatedQuery)
    }
    // the service returns only the ids of the category, we need to map the other data like the name
    const filtersCategories = result.Items.map((f) => f.Category)
    const allCategories = await getAllCategories(get, set, filtersCategories)
    const mappedFilters = {
      ...result,
      Items: result.Items.map((f) => {
        return {
          ...f,
          Category: allCategories.find((c) => c.Id === f.Category),
        }
      }),
    }
    set(filtersPagedListAtom, mappedFilters)
    set(pageState, statusUpdater('ready'))
  } catch (e) {
    console.error('### error', e)
    logAppError({
      Component: 'UnifiedFiltersListPage',
      Method: 'searchFilters',
      appError: e instanceof Error ? e : undefined,
    })
    set(pageState, errorUpdater)
    throw e
  }
}

export const copyFilterCallback = async (
  get: Getter,
  set: Setter,
  id: number,
): Promise<void> => {
  set(pageState, loaderUpdater(true))
  try {
    await copyFilter(id)
    await searchFilters(get, set)
    set(pageState, statusUpdater('ready'))
    notifySuccess('Load.Alerts.SuccessCopy')
  } catch (e) {
    console.error('### error', e)
    set(pageState, errorUpdater)
  }
  set(pageState, loaderUpdater(false))
}

export const changeFiltersQueryAndSearch = async (
  get: Getter,
  set: Setter,
  part: Partial<FilterPagedListQuery>,
): Promise<void> => {
  set(filtersQueryAtom, (prev) => {
    return { ...prev, ...part }
  })
  await searchFilters(get, set)
}

export const changeFiltersPage = async (
  get: Getter,
  set: Setter,
  page: number,
): Promise<void> => {
  await changeFiltersQueryAndSearch(get, set, { pageNumber: page })
}

export const changeFiltersPageSize = async (
  get: Getter,
  set: Setter,
  size: number,
): Promise<void> => {
  await changeFiltersQueryAndSearch(get, set, { pageNumber: 1, pageSize: size })
}

export const changeFiltersFieldOrdering = async (
  get: Getter,
  set: Setter,
  fieldName: string,
): Promise<void> => {
  const oldQuery = get(filtersQueryAtom)
  const newQuery = {
    orderBy: fieldName,
    orderDescending:
      oldQuery.orderBy === fieldName
        ? !(oldQuery.orderDescending ?? false)
        : false,
    pageNumber: 1,
  }
  await changeFiltersQueryAndSearch(get, set, newQuery)
}
